import { cycleInfo, cycleOn, cycleRegularity, hormonal } from "./cycle";
import { parseDay } from "./date";
import { hasCondition, pregnancy } from "./eating";
import type { Block, KayaState, Session } from "./types";

/**
 * Who the cycle may speak to, and how (ADR-057). `plan.ts` reads this, so it imports nothing
 * from the plan: `cycleTraining.ts` builds the day's suggestion on top of it.
 *
 * - "off": tracking off, periods stopped (ADR-039) or a man's profile — nothing at all.
 * - "pregnant": one line from ACOG and no cycle lines; a pregnancy has no phases.
 * - "symptoms": what she logs changes a session, but no line ever names a phase — a hormonal
 *   method has no natural phases, and counting days cannot place ovulation in an irregular cycle
 *   or with PCOS (McNulty 2020, Elliott-Sale 2021).
 * - "phases": a natural, regular cycle — the premenstrual line, Cycle care's walk and the luteal
 *   water line may show as well.
 */
export type CycleGate = "off" | "pregnant" | "symptoms" | "phases";

export function cycleGate(S: KayaState, ds: string): CycleGate {
  if (S.profile.sex === "male" || !cycleOn(S)) return "off";
  if (pregnancy(S) === "pregnant") return "pregnant";
  if (hormonal(S) || hasCondition(S, "pcos")) return "symptoms";
  const reg = cycleRegularity(S, parseDay(ds).getTime());
  if (reg.irregular || reg.late || reg.absent) return "symptoms";
  return "phases";
}

/** A premenstrual day in a cycle whose phases Kaya may name: Cycle care gains its walk. */
export function careWalkDay(S: KayaState, ds: string): boolean {
  return cycleGate(S, ds) === "phases" && !!cycleInfo(S, ds)?.pms;
}

/**
 * Ten minutes' walk, last in Cycle care on a premenstrual day. Aerobic exercise is the
 * best-studied treatment for PMS (Pearce 2020) and Cycle care alone is mostly breath and
 * stretching. Appended, never inserted, so the ticks of the blocks before it keep their indices.
 */
export const CARE_WALK: Block = {
  title: "A walk, if you can",
  rounds: [1, 1, 1],
  rest: [5, 5, 5],
  items: [{ id: "walk", t: [600, 600, 600], cue: "an easy pace, outside or around the house" }],
};

export function withCareWalk(s: Session): Session {
  return { ...s, mins: s.mins + 10, blocks: [...s.blocks, CARE_WALK] };
}
